/**
 * DashboardPlanCard - Plan/Credit overview card (top-right of the stats row).
 * Shows plan + renewal countdown, a segmented credit bar (Plan / Top-ups /
 * free) with legend, "credits this cycle" and the storage line.
 * Owned by Dashboard Subsystem 2. i18n namespace: `dashboardPlan.*`.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { getCreditBalance } from '@/lib/api/credits';
import { getLocale } from '@/lib/utils';
import { EID } from '@/lib/element-ids.registry';
import { DashboardCreditSegments } from './DashboardCreditSegments';

interface DashboardPlanCardProps {
  planName: string;
  discountPercentage: number;
  productCount?: number;
  productLimit?: number;
}

function daysUntil(iso: string): number {
  const end = new Date(iso);
  const diff = end.getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86_400_000));
}

export function DashboardPlanCard({ planName, discountPercentage }: DashboardPlanCardProps) {
  const { t } = useTranslation();
  const { data } = useQuery({ queryKey: ['credit-balance'], queryFn: getCreditBalance, staleTime: 30_000 });
  const b = data?.balance;

  const used = b?.monthly_used ?? 0;
  const total = b?.monthly_allocated ?? 0;
  const hasBuckets =
    b != null &&
    b.subscription_credits != null &&
    b.purchased_credits != null &&
    b.rollover_credits != null;
  const renewalIso = b?.billing_period_end ?? null;
  const renewalDate = renewalIso
    ? new Date(renewalIso).toLocaleDateString(getLocale(), { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <div
      id={EID['dashboard-plan-section']}
      className="dashboard-plan rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-neutral-900 dark:text-white">
              {planName}-{t('dashboardPlan.planLine')}
              {discountPercentage > 0 && (
                <span className="ml-1.5 text-emerald-500 font-medium">&minus;{discountPercentage}%</span>
              )}
            </p>
            {renewalDate && (
              <p data-testid="dashboard-plan-renewal" className="text-xs text-neutral-500">
                {t('dashboardPlan.renewedOn', { date: renewalDate })} ·{' '}
                {t('dashboardPlan.daysLeft', { count: daysUntil(renewalIso as string) })}
              </p>
            )}
          </div>
        </div>
        <Link
          id={EID['dashboard-plan-upgrade-link']}
          to="/v2/credits"
          className="text-xs text-primary-500 hover:text-primary-400 transition-colors"
        >
          {t('dashboardPlan.upgrade')}
        </Link>
      </div>

      {hasBuckets ? (
        <DashboardCreditSegments
          plan={b.subscription_credits}
          topup={b.purchased_credits}
          free={b.rollover_credits}
        />
      ) : (
        <div className="mb-3 h-2 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary-500 to-primary-400"
            style={{ width: `${total > 0 ? Math.min((used / total) * 100, 100) : 0}%` }}
          />
        </div>
      )}

      <div data-testid="dashboard-plan-cycle" className="flex items-center justify-between text-xs mb-3">
        <span className="text-neutral-500">{t('dashboardPlan.creditsThisCycle')}</span>
        <span className="font-mono text-neutral-600 dark:text-neutral-400">
          {new Intl.NumberFormat(getLocale()).format(used)} /{' '}
          {new Intl.NumberFormat(getLocale()).format(total)}
        </span>
      </div>

      <Link
        id={EID['dashboard-plan-topup-btn']}
        to="/v2/credits"
        className="block w-full mb-3 rounded-lg bg-primary-500 hover:bg-primary-400 text-white text-xs font-medium text-center py-2 transition-colors"
      >
        {t('dashboardPlan.topUp')}
      </Link>

      {b?.storage && (
        <div
          data-testid="dashboard-plan-storage"
          className="flex items-center justify-between text-xs pt-3 border-t border-neutral-100 dark:border-neutral-800"
        >
          <span className="text-neutral-500">{t('dashboardPlan.storage')}</span>
          <span className="font-mono text-neutral-600 dark:text-neutral-400">
            {b.storage.used_gb.toFixed(1)} / {b.storage.limit_gb} GB
          </span>
        </div>
      )}
    </div>
  );
}
